// useAwaitingSync — grace-гейт для read-after-write лага PowerSync.
//
// Проблема: сущности создаются через tRPC (INSERT в Postgres), а detail-страницы
// читают их через PowerSync (локальная SQLite). После create→navigate строка
// ещё не синкнулась в SQLite, но PowerSync-запрос уже НЕ `isLoading` (тот же
// SQL смонтирован в списке/диалоге → закеширован) → `row === undefined` →
// страница мгновенно показывает ErrorState, хотя строка приедет через ~1-3с.
//
// Хук возвращает true, пока строки нет, но окно ожидания синка не истекло —
// в это время показываем skeleton, а не ErrorState. Как только строка приехала
// (`present` стал true) или окно истекло — возвращает false. Само-восстановление:
// даже если строка приедет ПОСЛЕ окна, present=true сразу снимает гейт.
//
// КРИТИЧНО для свежих аккаунтов (инцидент 2026-06-15): на только что
// зарегистрированном пользователе ПЕРВИЧНЫЙ PowerSync-синк (установка соединения
// + загрузка начальных бакетов) занимает заметно дольше 6с. Если отсчитывать
// окно с момента mount, оно «сгорает» до того, как материал вообще успел
// приехать в SQLite → пользователь видит ложную «Ошибку обработки» на полностью
// готовом материале (view_count так и остаётся 0 — он просто не дошёл до экрана).
// Поэтому окно read-after-write начинаем отсчитывать ТОЛЬКО после `hasSynced`
// (первичный синк завершён). Абсолютный потолок страхует от зависшего/офлайн
// синка, чтобы skeleton не висел вечно.
//
// Эталон лага — `recordings/new.tsx` (goToDetail с задержкой 1000мс).

import { useEffect, useState } from "react";
import { useOnlineStatus } from "./use-online-status.js";
import { usePowerSyncStatus } from "./use-powersync-status.js";

// Абсолютный потолок ожидания: даже если первичный синк не пришёл (офлайн,
// упавшее соединение) — не держим skeleton дольше этого времени, показываем
// ErrorState честно.
const HARD_CEILING_MS = 20_000;

/**
 * @param present — сущность уже доступна локально (строка найдена).
 * @param graceMs — окно ожидания PowerSync-синка ПОСЛЕ первичного `hasSynced`
 *                  (по умолчанию 6с).
 * @returns true пока ждём синк (показывать loading), false когда строка есть
 *          или окно истекло (можно показать ErrorState).
 */
export function useAwaitingSync(present: boolean, graceMs = 6000): boolean {
	const { hasSynced } = usePowerSyncStatus();
	const isOnline = useOnlineStatus();
	const [expired, setExpired] = useState(false);

	useEffect(() => {
		if (present) return;
		setExpired(false);

		// Без сети синк не придёт — держать скелетон 20 секунд бессмысленно.
		// Экран должен сразу сказать, что локальной копии нет.
		if (!isOnline) {
			setExpired(true);
			return;
		}

		// Окно read-after-write отсчитываем только когда первичный синк уже
		// завершён — иначе на свежем аккаунте оно сгорит до прихода строки.
		const graceTimer = hasSynced ? window.setTimeout(() => setExpired(true), graceMs) : undefined;
		// Страховочный потолок: запускается всегда (в т.ч. пока hasSynced=false),
		// чтобы зависший/офлайн синк не оставил пользователя в вечном skeleton.
		const ceilingTimer = window.setTimeout(() => setExpired(true), HARD_CEILING_MS);

		return () => {
			if (graceTimer !== undefined) window.clearTimeout(graceTimer);
			window.clearTimeout(ceilingTimer);
		};
	}, [present, hasSynced, graceMs, isOnline]);

	return !present && !expired;
}
